/**
 * `parseDateInput`：把用户键盘敲的各种「反人类」日期写法解析成 `Date`（LeapWorldTwin-145）。
 * 来源：dzy 2026-09-06 反馈「日期填写需要一个个打年月日」——`DateField`（`@bf/ui`）消费本函数做
 * 即时解析与规范化回显，本文件只管解析，不碰任何渲染/DOM。
 *
 * 支持写法（按尝试顺序，第一个匹配的分支生效）：
 *   1. `YYYY-MM-DD` / `YYYY-M-D`             完整年月日，短横线
 *   2. `YYYY/M/D`                            完整年月日，斜杠
 *   3. `M/D`                                 当年，斜杠（两位年故意不支持——见下）
 *   4. `YYYYMMDD`（8 位数字）                 完整年月日，无分隔符
 *   5. `MMDD`（4 位数字）                     当年，无分隔符
 *   6. `今天` / `明天` / `昨天`               相对今天 ±1 天
 *   7. `+N` / `-N`                           相对今天 ±N 天（N 任意位数）
 *   8. `YYYY年M月D日` / `M月D日`（当年）      中文全角/半角数字均可
 *
 * **两位年故意拒绝**：所有「年」位置的正则都锁死 `\d{4}`，`26-9-6` / `9/6/26` 这类会直接落空到
 * `unrecognized-format`，不会被当成 2026 年或被 M/D 分支误吞（M/D 分支只接受恰好两段）。
 * 这是产品裁决（避免「06 年」到底是 2006 还是 1906 的歧义），不是遗漏。
 *
 * 日历有效性用显式的 `daysInMonth`/闰年判断校验，不依赖 `new Date(y, m, d)` 的自动进位——
 * 后者会把 `2026-02-30` 静默滚成 `2026-03-02`，那不是「解析失败」，是「悄悄解析成别的日期」，
 * 对日期输入场景是更危险的错误。
 */

export type DateParseFailureReason =
  | 'empty'
  | 'unrecognized-format'
  | 'invalid-calendar-date'
  | 'two-digit-year-rejected'

export type DateParseOptions = {
  /** 相对写法（今天/明天/+N/当年 M-D）的基准日；默认 `new Date()`。测试与快照必须显式传入，避免跨天翻转。 */
  today?: Date
  /** 预留：未来分流「中文关键词是否识别」等地区差异；当前恒为中文关键词识别，不区分取值。 */
  locale?: 'zh' | 'en'
}

export type DateParseResult =
  | { date: Date; reason: null }
  | { date: null; reason: DateParseFailureReason }

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function daysInMonth(year: number, month: number): number {
  return month === 2 && isLeapYear(year) ? 29 : MONTH_DAYS[month - 1]!
}

/** 午夜起点的本地日期，年月日范围先过再落地，避免 `Date` 构造器自动进位掩盖非法输入。 */
function buildDate(year: number, month: number, day: number): Date | null {
  if (year < 1 || year > 9999) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(year, month)) return null
  const date = new Date(year, month - 1, day)
  date.setHours(0, 0, 0, 0)
  return date
}

function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function addDays(date: Date, delta: number): Date {
  const copy = startOfDay(date)
  copy.setDate(copy.getDate() + delta)
  return copy
}

/** 全角数字/符号先转半角，键盘输入法误切换全角时不因此判失败。 */
function toHalfWidth(text: string): string {
  return text.replace(/[０-９－／＋]/g, (ch) => {
    const code = ch.charCodeAt(0)
    if (code === 0xff0d) return '-'
    if (code === 0xff0f) return '/'
    if (code === 0xff0b) return '+'
    return String.fromCharCode(code - 0xfee0)
  })
}

export function parseDateInput(text: string, options?: DateParseOptions): DateParseResult {
  const today = options?.today ? startOfDay(options.today) : startOfDay(new Date())
  const raw = toHalfWidth(text ?? '').trim()
  if (!raw) return { date: null, reason: 'empty' }

  const fail = (reason: DateParseFailureReason): DateParseResult => ({ date: null, reason })
  const ok = (date: Date | null): DateParseResult => (date ? { date, reason: null } : fail('invalid-calendar-date'))

  // 1. YYYY-M-D
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) return ok(buildDate(Number(m[1]), Number(m[2]), Number(m[3])))

  // 2. YYYY/M/D
  m = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/)
  if (m) return ok(buildDate(Number(m[1]), Number(m[2]), Number(m[3])))

  // 3. M/D（当年，两位年在此拒绝：只接受恰好两段）
  m = raw.match(/^(\d{1,2})\/(\d{1,2})$/)
  if (m) return ok(buildDate(today.getFullYear(), Number(m[1]), Number(m[2])))

  // 4. YYYYMMDD（8 位）
  m = raw.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (m) return ok(buildDate(Number(m[1]), Number(m[2]), Number(m[3])))

  // 5. MMDD（4 位，当年）
  m = raw.match(/^(\d{2})(\d{2})$/)
  if (m) return ok(buildDate(today.getFullYear(), Number(m[1]), Number(m[2])))

  // 6. 中文关键词
  if (raw === '今天') return ok(today)
  if (raw === '明天') return ok(addDays(today, 1))
  if (raw === '昨天') return ok(addDays(today, -1))

  // 7. +N / -N（相对今天，天）
  m = raw.match(/^([+-])(\d+)$/)
  if (m) return ok(addDays(today, (m[1] === '-' ? -1 : 1) * Number(m[2])))

  // 8. 中文全角日期：YYYY年M月D日 / M月D日（当年）
  m = raw.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日$/)
  if (m) return ok(buildDate(Number(m[1]), Number(m[2]), Number(m[3])))
  m = raw.match(/^(\d{1,2})月(\d{1,2})日$/)
  if (m) return ok(buildDate(today.getFullYear(), Number(m[1]), Number(m[2])))

  // 两位年显式识别一次，给出比 unrecognized-format 更精确的原因（26-9-6 / 9/6/26 一类）
  if (/^\d{2}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw) || /^\d{1,2}[-/]\d{1,2}[-/]\d{2}$/.test(raw)) {
    return fail('two-digit-year-rejected')
  }

  return fail('unrecognized-format')
}

/** `DateField` 失焦规范化 / 下方回显用：`2026-09-06` + 星期，中文周几。 */
const WEEKDAY_ZH = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export function formatParsedDatePreview(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d} ${WEEKDAY_ZH[date.getDay()]}`
}

/** `<input type="date">` 原生控件与受控 value 用的 `YYYY-MM-DD`，与 `formatParsedDatePreview` 分开是因为一个要人读一个要机器读。 */
export function toISODateOnly(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** 服务端 ISO（UTC）回填 `datetime-local` 表单用：取浏览器本地时间的 `YYYY-MM-DDTHH:mm`，和提交时 `new Date(value).toISOString()` 对称。 */
export function toDateTimeLocalValue(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${toISODateOnly(date)}T${h}:${min}`
}

/**
 * `datetime-local` 替换点用：工单裁决「暂用 `DateField` + 现有时间输入组合，不做时间选择器」，
 * 拆/合两个纯函数落在这里而不是各 app 各写一份——多个 app 的 `datetime-local` 字段都要拆分
 * 日期/时间两段，属于 rule of two 内的格式化逻辑，不是业务规则。
 */
export function splitDateTimeLocal(value: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' }
  const [date = '', time = ''] = value.split('T')
  return { date, time: time.slice(0, 5) }
}

export function joinDateTimeLocal(date: string, time: string): string {
  if (!date) return ''
  return `${date}T${time || '00:00'}`
}
